import React, { useLayoutEffect, useRef, useState } from "react";

/// Shared geometry for the main-app list pages (Overview, Datasets,
/// Connections, Trash, Access). Every page's column starts at the same height,
/// so navigating between them doesn't shift the search bar.

/// Overview carries a hero and a create strip around its list, so its list gets
/// a shorter cap to keep the total column comparable.
/// Rows there are `--item-height` (2rem) tall.
export const OVERVIEW_LIST_ROWS = 8;

/// Pins the column top 30% down, whatever this page holds — so content only
/// ever grows downward. Rises on a short pane so a full column (~30rem) fits.
export const ColumnTopSpacer: React.FC = () => (
  <div
    style={{
      flexShrink: 0,
      height: "max(2.5rem, min(30%, 100% - 30rem))",
    }}
  />
);

/// A list page's scroll box: content height up to `rows` rows — nine on every
/// page but Overview, so the columns land in one height band — gliding between
/// sizes. Overflow stays hidden below the cap so no scrollbar flashes mid-glide.
export const ListBox: React.FC<
  React.ComponentProps<"div"> & { rows?: number }
> = ({ rows = 9, children, ...rest }) => {
  const inner = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number>();
  const [scrolls, setScrolls] = useState(false);
  useLayoutEffect(() => {
    const content = inner.current!;
    const measure = () => {
      const cap = parseFloat(
        getComputedStyle(content.parentElement!).maxHeight,
      );
      setHeight(content.offsetHeight > cap ? cap : content.offsetHeight);
      setScrolls(content.offsetHeight > cap);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(content);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      {...rest}
      className={`overflow-x-hidden ${scrolls ? "overflow-y-auto pr-1" : "overflow-y-hidden"}`}
      style={{
        maxHeight: `calc(var(--item-height) * ${rows})`,
        height,
        transition: "height var(--duration-medium) var(--ease-move)",
      }}
    >
      <div ref={inner}>{children}</div>
    </div>
  );
};
